import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';
import { useContext } from 'react';

import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';

import ApproveChargesPageContext from '../ApproveChargesPageContext';
import { ChargesGrainTab } from '../approveChargesTabs';
import {
  CHARGES_CELL_SX,
  CHARGES_GRID_TEMPLATE_COLUMNS,
  chargesGridColumn,
} from '../chargesGrid';

const jobHeaders = [
  [chargesGridColumn.date, () => t('common.date')],
  [chargesGridColumn.order, () => t('common.order')],
  [
    chargesGridColumn.customerAndProject,
    () => t('approvals.approve_charges.columns.customer_and_project'),
  ],
  [
    chargesGridColumn.driverAndVendor,
    () => t('approvals.files.jobs_table.headers.driver_and_vendor'),
  ],
  [chargesGridColumn.truck, () => t('form_fields.truck')],
  [
    chargesGridColumn.route,
    () => t('approvals.approve_charges.columns.pickup_to_dropoff'),
  ],
  [chargesGridColumn.material, () => t('approvals.files.loads_table.headers.material')],
  [chargesGridColumn.loads, () => t('common.loads')],
  [
    chargesGridColumn.ticketQuantity,
    () => t('approvals.files.jobs_table.headers.total_quantity'),
  ],
  [
    chargesGridColumn.payableAmount,
    () => t('approvals.files.jobs_table.headers.payables'),
  ],
  [
    chargesGridColumn.receivableAmount,
    () => t('approvals.files.jobs_table.headers.receivables'),
  ],
  [
    chargesGridColumn.payableNumber,
    () => t('approvals.approve_charges.columns.pay_number'),
  ],
  [
    chargesGridColumn.invoiceNumber,
    () => t('approvals.approve_charges.columns.invoice_number'),
  ],
] as const;

const approveColumnKeys = {
  [ChargesGrainTab.HOURLY_JOBS]: {
    title: 'approvals.approve_charges.columns.approve_job',
    tooltip: 'approvals.approve_charges.columns.approve_job_tooltip',
  },
  [ChargesGrainTab.LOADS]: {
    title: 'approvals.approve_charges.columns.approve_loads',
    tooltip: null,
  },
} satisfies Record<ChargesGrainTab, { title: string; tooltip: string | null }>;

const timeAndDurationHeader = [
  chargesGridColumn.timeAndDuration,
  () => t('approvals.files.jobs_table.headers.time_and_duration'),
] as const;

export default function ChargesJobsHeader() {
  const { grainTab } = useContext(ApproveChargesPageContext);
  const headers =
    grainTab === ChargesGrainTab.HOURLY_JOBS
      ? jobHeaders.flatMap<(typeof jobHeaders)[number] | typeof timeAndDurationHeader>(
          (header) =>
            header[0] === chargesGridColumn.loads
              ? [timeAndDurationHeader, header]
              : [header],
        )
      : jobHeaders;
  const approveColumn = approveColumnKeys[grainTab];

  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.semantic.surfaceSunken,
        borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        display: 'grid',
        gridTemplateColumns: CHARGES_GRID_TEMPLATE_COLUMNS,
        pr: 0,
      })}
    >
      {headers.map(([column, title]) => (
        <Box key={column} sx={{ ...CHARGES_CELL_SX, gridColumn: column }}>
          <OverflowAwareText sx={{ fontWeight: 600 }}>{title()}</OverflowAwareText>
        </Box>
      ))}
      <Tooltip title={approveColumn.tooltip ? t(approveColumn.tooltip) : ''}>
        <Box sx={{ ...CHARGES_CELL_SX, gridColumn: chargesGridColumn.status }}>
          <OverflowAwareText sx={{ fontWeight: 600 }}>
            {t(approveColumn.title)}
          </OverflowAwareText>
        </Box>
      </Tooltip>
    </Box>
  );
}
